/* eslint-disable import/prefer-default-export */
import {
  ILEExamSession,
  TestletStatus,
  TestSessionCompletionStatus,
} from '@components/Component/LearnosityExams/types';
import { IUser } from '@CT-Types/user';
import { daysSinceCalendarDate, parseCalendarDate } from '@utils/datetime';
import { ROLE_TYPE } from '@utils/enums';

// Extended types to handle the actual data structure
interface SessionMetadataSection {
  section_id: number;
  section_status: string;
  is_complete: boolean;
  section_name?: string;
  section_date?: string;
  last_modified?: number;
}

interface ExtendedTestSession extends ILEExamSession {
  session_metadata?: {
    sections: SessionMetadataSection[];
  };
}

// Types
interface BannerData {
  bannerConditionPrecedence: number;
  bannerText: string | null;
  examId: string | null;
  bannerUrl?: string;
  bannerType?: string;
}

interface TestletCompletionStatus {
  assignedTestlet: boolean;
  unassignedTestlet: boolean;
  allExamsCompleted: boolean;
}

interface ExamBannerParams {
  courseId: string;
  sectionId: string;
}

interface ExamBannerContext {
  exams: ExtendedTestSession[];
  user: IUser;
  testSessionCompletionStatus: TestSessionCompletionStatus[];
  isLearnosityExam: boolean;
  params: ExamBannerParams;
}

interface BannerRule {
  priority: number;
  name: string;
  canApply: (exam: ExtendedTestSession, context: ExamBannerContext) => boolean;
  createBanner: (exam: ExtendedTestSession, context: ExamBannerContext) => BannerData;
}

// Constants
const BANNER_CONDITIONS = {
  ASSIGNED_TESTLET: 10,
  UNASSIGNED_TESTLET: 20,
  UPCOMING_EXAM: 30,
  LE_NOT_STARTED_TESTLET: 19,
} as const;

const EXAM_DAYS_LIMIT = 14;
const UPCOMING_EXAM_DAYS = 3;

const hasAssignedExamV2Testlets = (exam: ExtendedTestSession): boolean => {
  if (!exam.session_metadata?.sections) {
    return false;
  }

  return exam.session_metadata.sections.some(
    (section) => section.section_status === 'Unlocked' && !section.is_complete,
  );
};

const hasCompletedTestlets = (
  testletSessions: TestSessionCompletionStatus[],
  examId: string,
): boolean => {
  const examSessions = testletSessions?.filter(
    (status) => status.exam_session_id.toString() === examId.toString(),
  );

  // If no sessions found for this exam, consider it not completed
  if (!examSessions || examSessions.length === 0) {
    return false;
  }

  return examSessions.every((status) => status.is_complete);
};

const getExamV2TestletCompletionStatus = (
  exam: ExtendedTestSession,
): TestletCompletionStatus => {
  const initialStatus: TestletCompletionStatus = {
    assignedTestlet: false,
    unassignedTestlet: false,
    allExamsCompleted: true,
  };

  if (!exam.session_metadata?.sections) {
    return initialStatus;
  }

  return exam.session_metadata.sections.reduce(
    (acc, section) => {
      if (!section.is_complete) {
        if (section.section_status !== 'Locked') {
          acc.assignedTestlet = true;
        } else {
          acc.unassignedTestlet = true;
        }
        acc.allExamsCompleted = false;
      }
      return acc;
    },
    { ...initialStatus },
  );
};

const getLEStudentUnlockedExam = (
  testSessionCompletionStatus: TestSessionCompletionStatus[],
  testSession: ILEExamSession,
) => {
  const unlockedTestlets = testSession.testlets.filter(
    (testlet) => testlet.status === TestletStatus.UNLOCKED,
  );

  return unlockedTestlets.filter(
    (testlet) =>
      !testSessionCompletionStatus.some(
        (status) => status.testlet_id === testlet.id && status.is_complete,
      ),
  );
};

const getTestUrl = (
  {
    examId,
    courseId,
    sectionId,
    testletId,
  }: {
    examId: string;
    courseId: string;
    sectionId: string;
    testletId?: string;
  },
  hasLearnosityExamAccess = true,
): string => {
  if (!hasLearnosityExamAccess) {
    return `/test/course/${courseId}/section/${sectionId}/exam/${examId}`;
  }

  if (testletId) {
    return `/tests/course/${courseId}/section/${sectionId}/testsession/${examId}/testlet/${testletId}`;
  }

  return `/tests/course/${courseId}/section/${sectionId}/testsession/${examId}`;
};

const getLEStudentUnlockedExamBannerObject = (
  testSessionCompletionStatus: TestSessionCompletionStatus[],
  testSession: ILEExamSession,
  params: ExamBannerParams,
): BannerData | null => {
  const unlockedTestlets = getLEStudentUnlockedExam(
    testSessionCompletionStatus,
    testSession,
  );

  if (unlockedTestlets.length === 0) {
    return null;
  }

  return {
    bannerConditionPrecedence: BANNER_CONDITIONS.ASSIGNED_TESTLET,
    bannerText:
      'Your Teacher has assigned you Practice Testlets. Please complete all assigned Testlets before continuing to use ChalkTalk',
    examId: testSession.id.toString(),
    bannerUrl: getTestUrl(
      {
        examId: testSession.id.toString(),
        courseId: params.courseId,
        sectionId: params.sectionId,
        testletId: unlockedTestlets[0].id.toString(),
      },
      true,
    ),
    bannerType: 'alert',
  };
};

const sortExamsByPriority = (exams: ExtendedTestSession[]): ExtendedTestSession[] =>
  [...exams].sort((a, b) => {
    // Sort by practice_test_order if available
    if (b.practice_test_order !== undefined && a.practice_test_order !== undefined) {
      return b.practice_test_order - a.practice_test_order;
    }

    // Fallback to start_date sorting
    if (b.start_date !== undefined && a.start_date !== undefined) {
      const bDate = parseCalendarDate(b.start_date)?.getTime() ?? 0;
      const aDate = parseCalendarDate(a.start_date)?.getTime() ?? 0;
      return bDate - aDate;
    }

    return 0;
  });

const calculateDaysSinceExam = (examStartDate: string): number =>
  daysSinceCalendarDate(examStartDate);

export const getExamTimeInfo = (examStartDate: string) => {
  const daysSince = calculateDaysSinceExam(examStartDate);
  const isWithinLimit = daysSince <= EXAM_DAYS_LIMIT && daysSince >= -UPCOMING_EXAM_DAYS;
  const hasStarted = daysSince >= 0;

  return { daysSince, isWithinLimit, hasStarted };
};

const isUpcomingExam = (daysSinceExam: number): boolean =>
  daysSinceExam >= -UPCOMING_EXAM_DAYS && daysSinceExam < 0;

const isExamStarted = (daysSinceExam: number): boolean => daysSinceExam >= 0;

class BannerRulesFactory {
  private static sortedRules: BannerRule[] | null = null;

  private static rules: BannerRule[] = [
    // Instructor Rules
    {
      priority: BANNER_CONDITIONS.LE_NOT_STARTED_TESTLET,
      name: 'INSTRUCTOR_LE_NOT_STARTED_TESTLET',
      canApply: (exam: ExtendedTestSession, context: ExamBannerContext) => {
        if (context.user.role !== ROLE_TYPE.INSTRUCTOR) {
          return false;
        }

        if (!context.isLearnosityExam) {
          return false;
        }
        const { isWithinLimit, hasStarted } = getExamTimeInfo(exam.start_date);
        if (!isWithinLimit || !hasStarted) {
          return false;
        }

        const firstNotStartedTestlet = (exam as ILEExamSession).testlets.some(
          (testlet) => testlet.status === TestletStatus.NOT_STARTED,
        );
        return !!firstNotStartedTestlet;
      },
      createBanner: (exam: ExtendedTestSession, context: ExamBannerContext) => ({
        bannerConditionPrecedence: BANNER_CONDITIONS.LE_NOT_STARTED_TESTLET,
        bannerText:
          'Scheduled practice tests have begun and you have not started one or more testlets. Please unlock the testlets and have your students take all testlets before continuing to use ChalkTalk',
        examId: exam.id.toString(),
        bannerUrl: getTestUrl({
          examId: exam.id.toString(),
          courseId: context.params.courseId,
          sectionId: context.params.sectionId,
          testletId:
            context.exams
              .find((e) => e.id === exam.id)
              ?.testlets.find((t) => t.status === TestletStatus.NOT_STARTED)
              ?.id.toString() || undefined,
        }),
        bannerType: 'alert',
      }),
    },
    {
      priority: BANNER_CONDITIONS.ASSIGNED_TESTLET,
      name: 'INSTRUCTOR_ASSIGNED_TESTLET',
      canApply: (exam: ExtendedTestSession, context: ExamBannerContext) => {
        if (context.user.role !== ROLE_TYPE.INSTRUCTOR) {
          return false;
        }

        if (context.isLearnosityExam) {
          return false;
        }

        const { assignedTestlet } = getExamV2TestletCompletionStatus(exam);
        const hasAnsweredAllTestlets = hasCompletedTestlets(
          context.testSessionCompletionStatus,
          exam.id.toString(),
        );
        return assignedTestlet && !hasAnsweredAllTestlets;
      },
      createBanner: (exam: ExtendedTestSession, context: ExamBannerContext) => ({
        bannerConditionPrecedence: BANNER_CONDITIONS.ASSIGNED_TESTLET,
        bannerText:
          'You have students that have not completed their assigned Testlets. Please make sure all students complete their testlets before continuing to use ChalkTalk',
        examId: exam.id.toString(),
        bannerUrl: getTestUrl(
          {
            examId: exam.id.toString(),
            courseId: context.params.courseId,
            sectionId: context.params.sectionId,
          },
          false,
        ),
        bannerType: 'alert',
      }),
    },
    {
      priority: BANNER_CONDITIONS.UNASSIGNED_TESTLET,
      name: 'INSTRUCTOR_UNASSIGNED_TESTLET',
      canApply: (exam: ExtendedTestSession, context: ExamBannerContext) => {
        if (context.user.role !== ROLE_TYPE.INSTRUCTOR) {
          return false;
        }

        if (context.isLearnosityExam) {
          return false;
        }

        const { unassignedTestlet } = getExamV2TestletCompletionStatus(exam);
        const { daysSince } = getExamTimeInfo(exam.start_date);
        const examHasStarted = isExamStarted(daysSince);
        const hasAnsweredAllTestlets = hasCompletedTestlets(
          context.testSessionCompletionStatus,
          exam.id.toString(),
        );

        if (context.isLearnosityExam) {
          const firstNotStartedTestlet = (exam as ILEExamSession).testlets.find(
            (testlet) => testlet.status === TestletStatus.NOT_STARTED,
          );
          return !hasAnsweredAllTestlets && !!firstNotStartedTestlet;
        }

        return examHasStarted && unassignedTestlet && !hasAnsweredAllTestlets;
      },
      createBanner: (exam: ExtendedTestSession, context: ExamBannerContext) => ({
        bannerConditionPrecedence: BANNER_CONDITIONS.UNASSIGNED_TESTLET,
        bannerText:
          'Scheduled practice tests have begun and you have unopened Testlets. Please have your students take all testlets before continuing to use ChalkTalk',
        examId: exam.id.toString(),
        bannerUrl: getTestUrl(
          {
            examId: exam.id.toString(),
            courseId: context.params.courseId,
            sectionId: context.params.sectionId,
          },
          context.isLearnosityExam,
        ),
        bannerType: 'alert',
      }),
    },
    {
      priority: BANNER_CONDITIONS.UPCOMING_EXAM,
      name: 'INSTRUCTOR_UPCOMING_EXAM',
      canApply: (exam: ExtendedTestSession, context: ExamBannerContext) => {
        if (context.user.role !== ROLE_TYPE.INSTRUCTOR || context.isLearnosityExam) {
          return false;
        }

        const { allExamsCompleted } = getExamV2TestletCompletionStatus(exam);
        const { daysSince } = getExamTimeInfo(exam.start_date);
        const isUpcoming = isUpcomingExam(daysSince);

        return isUpcoming && !allExamsCompleted;
      },
      createBanner: (exam: ExtendedTestSession, context: ExamBannerContext) => ({
        bannerConditionPrecedence: BANNER_CONDITIONS.UPCOMING_EXAM,
        bannerText: 'There are upcoming Practice Tests',
        examId: exam.id.toString(),
        bannerUrl: getTestUrl(
          {
            examId: exam.id.toString(),
            courseId: context.params.courseId,
            sectionId: context.params.sectionId,
          },
          context.isLearnosityExam,
        ),
        bannerType: 'alert',
      }),
    },
    // Student Rules
    {
      priority: BANNER_CONDITIONS.ASSIGNED_TESTLET,
      name: 'STUDENT_ASSIGNED_TESTLET',
      canApply: (exam: ExtendedTestSession, context: ExamBannerContext) => {
        if (context.user.role !== ROLE_TYPE.STUDENT) {
          return false;
        }

        if (context.isLearnosityExam) {
          const unlockedTestlets = getLEStudentUnlockedExam(
            context.testSessionCompletionStatus,
            exam as ILEExamSession,
          );
          return unlockedTestlets.length > 0;
        }

        const assignedTestlets = hasAssignedExamV2Testlets(exam);
        const hasAnsweredAllTestlets = hasCompletedTestlets(
          context.testSessionCompletionStatus,
          exam.id.toString(),
        );
        return assignedTestlets && !hasAnsweredAllTestlets;
      },
      createBanner: (
        exam: ExtendedTestSession,
        context: ExamBannerContext,
      ): BannerData => {
        if (context.isLearnosityExam) {
          const banner = getLEStudentUnlockedExamBannerObject(
            context.testSessionCompletionStatus,
            exam as ILEExamSession,
            context.params,
          );
          if (!banner) {
            throw new Error('Expected banner to be created for Learnosity exam');
          }
          return banner;
        }

        return {
          bannerConditionPrecedence: BANNER_CONDITIONS.ASSIGNED_TESTLET,
          bannerText:
            'Your Teacher has assigned you Practice Testlets. Please complete all assigned Testlets before continuing to use ChalkTalk',
          examId: exam.id.toString(),
          bannerUrl: getTestUrl(
            {
              examId: exam.id.toString(),
              courseId: context.params.courseId,
              sectionId: context.params.sectionId,
            },
            context.isLearnosityExam,
          ),
          bannerType: 'alert',
        };
      },
    },
  ];

  static getRules(): BannerRule[] {
    if (!this.sortedRules) {
      this.sortedRules = [...this.rules].sort((a, b) => a.priority - b.priority);
    }
    return this.sortedRules;
  }

  static evaluate(
    exam: ExtendedTestSession,
    context: ExamBannerContext,
  ): BannerData | null {
    const rules = this.getRules();

    return (
      rules.find((rule) => rule.canApply(exam, context))?.createBanner(exam, context) ||
      null
    );
  }
}

// Main function
export const handleExamsBannerLogic = ({
  exams,
  user,
  testSessionCompletionStatus,
  isLearnosityExam,
  params,
}: ExamBannerContext): BannerData | null => {
  const { courseId, sectionId } = params;

  if (!courseId || !sectionId) {
    console.error('handleExamsBannerLogic:', 'courseId or sectionId is not defined');
    return null;
  }

  if (!exams || exams.length === 0) {
    return null;
  }

  const sortedExams = sortExamsByPriority(exams);
  const context: ExamBannerContext = {
    exams,
    user,
    testSessionCompletionStatus,
    isLearnosityExam,
    params,
  };

  // Evaluate rules for each exam with early termination
  const filteredExams = sortedExams.filter((exam) => {
    const { isWithinLimit } = getExamTimeInfo(exam.start_date);
    return isWithinLimit;
  });

  return filteredExams.reduce<BannerData | null>((result, exam) => {
    // Early termination: if we already found a banner, don't process more exams
    if (result) {
      return result;
    }

    const banner = BannerRulesFactory.evaluate(exam, context);
    return banner || null;
  }, null);
};
